import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLoader } from 'redux/slices/loader';
import { getPlans, cancelSubscription } from 'services/subscriptionService';
import { toast } from 'react-toastify';
import ConfirmationModal from 'Containers/Modal/ConfirmationModal/ConfirmationModal';
import Styles from './PricingTable.module.scss';

const PricingTable = ({
  plans,
  duration,
  ctas,
  currentSubscription,
  lastSubscription,
  onPlanSelect,
  context = 'page', // New prop to identify context: 'page', 'modal', 'drawer'
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [features, setFeatures] = useState([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Check localStorage for selected plan and auto-click it (only if user is logged in)
  useEffect(() => {
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan && plans && plans.length > 0 && auth?.isLoggedIn) {
      try {
        const planData = JSON.parse(storedPlan);
        // Find the matching plan in the current plans array
        const matchingPlan = plans.find((plan) => plan.id === planData.id);
        if (matchingPlan) {
          // Simulate button click for the stored plan
          handleButtonClick(matchingPlan);
        }
      } catch (error) {
        console.error('Error parsing stored plan:', error);
      }
    }
  }, [plans, auth?.isLoggedIn]); // Trigger when plans are loaded OR auth state changes

  const fetchPlans = useCallback(async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Loading Plans',
          description: 'Please wait...',
        }),
      );
      const plansRes = await getPlans();
      const { data } = plansRes || {};

      const { features: backendFeatures } = data || {};

      // Set features from backend
      if (backendFeatures && Array.isArray(backendFeatures)) {
        setFeatures(backendFeatures);
      }

      dispatch(setLoader({ isLoading: false }));
    } catch (e) {
      console.error('Error fetching plans:', e);
      dispatch(setLoader({ isLoading: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  if (!plans || plans.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <h3>No pricing plans available</h3>
        <p>Please check back later or contact support.</p>
      </div>
    );
  }

  // Filter to only show public plans (Basic, Pro, Business) - exclude Lifetime plans
  const filteredPlans = plans
    .filter((plan) => !['Lifetime Pro Plan', 'Lifetime Business Plan'].includes(plan.type))
    .slice(0, 3);

  const getFeatureValue = (feature, planType) => {
    if (planType === 'basicplan') return feature.basicValue;
    if (planType === 'proplan') return feature.proValue;
    if (planType === 'businessplan') return feature.businessValue;
    return false;
  };

  const renderFeatureCell = (feature, value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.6667 5L7.5 14.1667L3.33333 10"
            stroke="#00A7B7"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <span className={Styles.feature_text}>-</span>
      );
    }
    return <span className={Styles.feature_text}>{value}</span>;
  };

  const handleButtonClick = (plan) => {
    // Check if this is the current active subscription and should be cancelled
    const isCurrentPlan = currentSubscription?.stripe_price?.id === plan?.id;
    const shouldCancel =
      currentSubscription?.active &&
      isCurrentPlan &&
      currentSubscription?.status !== 'canceled';

    if (shouldCancel) {
      setCancelModalOpen(true);
      return;
    }

    // If onPlanSelect callback is provided (modal context), use it
    if (onPlanSelect) {
      onPlanSelect(plan);
      return;
    }

    // Store the selected plan in localStorage for the sign-up process
    const selectedPlanData = {
      id: plan.id,
      type: plan.type,
      amount: plan.amount,
      interval: plan.interval,
      duration: duration,
      timestamp: Date.now(),
    };
    localStorage.setItem('selectedPlan', JSON.stringify(selectedPlanData));

    // Check if user is logged in
    if (!auth?.isLoggedIn) {
      // User is not logged in, redirect to sign-up
      navigate('/sign-up');
      return;
    }
    // User is logged in, check subscription history
    const isNewUser = !currentSubscription && !lastSubscription;

    // For logged in users, navigate directly to payment with trial param for new users
    const trialParam = isNewUser ? '?trial=true' : '';
    navigate(`/payment${trialParam}`, {
      state: { subscriptionId: plan?.id },
    });
  };

  const handleCancelSubscription = async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Unsubscribing Plan!',
          description: 'Please wait...',
        }),
      );
      await cancelSubscription();
      toast.success('Your Subscription Was Cancelled');
      setCancelModalOpen(false);
      dispatch(setLoader({ isLoading: false }));
      // Refresh the page or refetch data
      window.location.reload();
    } catch (error) {
      dispatch(setLoader({ isLoading: false }));
      toast.error(error?.response?.data?.error || 'Something went wrong');
    }
  };

  const getButtonText = (plan) => {
    // Check if user has ever had a subscription (new user vs returning user)
    const isNewUser = !currentSubscription && !lastSubscription;

    // Check if this is the current active subscription
    const isCurrentPlan = currentSubscription?.stripe_price?.id === plan?.id;

    if (
      currentSubscription?.active &&
      isCurrentPlan &&
      currentSubscription?.status !== 'canceled'
    ) {
      return 'Cancel Subscription';
    } else if (currentSubscription?.active) {
      return 'Select Plan';
    } else if (isNewUser) {
      return 'Start Your 3-Day Trial';
    } else {
      return 'Select Plan';
    }
  };

  const getBestSuitedText = (planType) => {
    switch (planType?.toLowerCase().replace(/\s/g, '')) {
      case 'basicplan':
        return 'Solopreneurs just starting out.';
      case 'proplan':
        return 'Marketers or entrepreneurs growing their business & personal brands.';
      case 'businessplan':
        return 'Teams managing multiple brands across multiple channels.';
      default:
        return '';
    }
  };

  const renderMobileCards = () => {
    return (
      <div className={Styles.mobile_cards}>
        {filteredPlans.map((plan, index) => {
          const discountedPerMonth =
            duration === 'year' ? Math.floor(plan.amount / 12) : plan.amount;
          const originalMonthlyPrice = plan.discounted_amount;
          const planType = plan.type?.replace(/\s/g, '').toLowerCase();

          // Get features that have a value for this plan
          const planFeatures = features.filter((feature) => {
            const value = getFeatureValue(feature, planType);
            return value && value !== false;
          });

          // Get features from previous plans to show inheritance
          const getPreviousPlanFeatures = () => {
            if (index === 1) {
              // Pro plan - get Basic plan features
              const basicPlanType = 'basicplan';
              return features.filter((feature) => {
                const value = getFeatureValue(feature, basicPlanType);
                return value && value !== false;
              });
            } else if (index === 2) {
              // Business plan - get Pro plan features
              const proPlanType = 'proplan';
              return features.filter((feature) => {
                const value = getFeatureValue(feature, proPlanType);
                return value && value !== false;
              });
            }
            return [];
          };

          const previousPlanFeatures = getPreviousPlanFeatures();

          return (
            <div
              key={plan.id}
              className={`${Styles.mobile_card} ${Styles[`${planType}_card`]}`}
            >
              {/* Card Header */}
              <div
                className={`${Styles.mobile_card_header} ${Styles[`${planType}_header`]}`}
              >
                <h3 className={Styles.mobile_plan_name}>{plan.type}</h3>
                <div className={Styles.mobile_price_container}>
                  <div className={Styles.mobile_price_display}>
                    {duration === 'year' &&
                    originalMonthlyPrice !== discountedPerMonth ? (
                      <>
                        <span className={Styles.mobile_original_price}>
                          ${originalMonthlyPrice}
                        </span>
                        <span className={Styles.mobile_price_amount}>
                          ${discountedPerMonth}
                        </span>
                      </>
                    ) : (
                      <span className={Styles.mobile_price_amount}>
                        ${discountedPerMonth}
                      </span>
                    )}
                  </div>
                  <span className={Styles.mobile_billing_info}>
                    /mo (billed {duration === 'month' ? 'monthly' : 'annually'})
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className={Styles.mobile_card_body}>
                {/* Include previous plan features note */}
                {index > 0 && (
                  <div className={Styles.included_features_note}>
                    This plan will include all {index === 1 ? 'Basic' : 'Pro'}{' '}
                    plan features.
                  </div>
                )}

                {/* Feature list */}
                <div className={Styles.mobile_features_container}>
                  <ul className={Styles.mobile_features_list}>
                    {planFeatures.map((feature, featureIndex) => {
                      const currentValue = getFeatureValue(feature, planType);

                      // For Basic plan (index 0), show all features
                      if (index === 0) {
                        return (
                          <li
                            key={featureIndex}
                            className={Styles.mobile_feature_item}
                          >
                            <span className={Styles.mobile_feature_check}>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 20 20"
                                fill="none"
                              >
                                <circle cx="10" cy="10" r="10" fill="#039855" />
                                <path
                                  d="m6 10 2 2 4-4"
                                  stroke="#fff"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span className={Styles.mobile_feature_text}>
                              {feature.type === 'boolean'
                                ? feature.label
                                : `${currentValue} ${feature.label}`}
                            </span>
                          </li>
                        );
                      }

                      // For Pro/Business plans, check if feature should be shown
                      const isPreviousPlanFeature = previousPlanFeatures.find(
                        (prevFeature) => prevFeature.id === feature.id,
                      );

                      if (isPreviousPlanFeature) {
                        // If it's a boolean feature and exists in previous plan, skip it
                        if (feature.type === 'boolean') {
                          return null;
                        }

                        // If it's a numeric feature, check if value is different
                        const previousPlanType =
                          index === 1 ? 'basicplan' : 'proplan';
                        const previousValue = getFeatureValue(
                          feature,
                          previousPlanType,
                        );

                        // If numeric values are different, show the feature
                        if (
                          feature.type !== 'boolean' &&
                          currentValue !== previousValue
                        ) {
                          return (
                            <li
                              key={featureIndex}
                              className={Styles.mobile_feature_item}
                            >
                              <span className={Styles.mobile_feature_check}>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <circle
                                    cx="10"
                                    cy="10"
                                    r="10"
                                    fill="#039855"
                                  />
                                  <path
                                    d="m6 10 2 2 4-4"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </span>
                              <span className={Styles.mobile_feature_text}>
                                {`${currentValue} ${feature.label}`}
                              </span>
                            </li>
                          );
                        }

                        // If numeric values are the same, skip
                        return null;
                      }

                      // Feature doesn't exist in previous plan, show it
                      return (
                        <li
                          key={featureIndex}
                          className={Styles.mobile_feature_item}
                        >
                          <span className={Styles.mobile_feature_check}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <circle cx="10" cy="10" r="10" fill="#039855" />
                              <path
                                d="m6 10 2 2 4-4"
                                stroke="#fff"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className={Styles.mobile_feature_text}>
                            {feature.type === 'boolean'
                              ? feature.label
                              : `${currentValue} ${feature.label}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Best Suited For */}
                <div className={Styles.mobile_best_suited}>
                  <p className={Styles.mobile_best_suited_text}>
                    <span className={Styles.mobile_best_suited_label}>
                      Best Suited For:
                    </span>{' '}
                    {getBestSuitedText(plan.type)}
                  </p>
                </div>
              </div>

              {/* Card Footer */}
              <div className={Styles.mobile_card_footer}>
                <button
                  className={`${Styles.mobile_trial_button} ${Styles[`${planType}_button`]} ${
                    getButtonText(plan) === 'Cancel Subscription'
                      ? Styles.cancel_subscription_button
                      : ''
                  }`}
                  onClick={() => handleButtonClick(plan)}
                >
                  {getButtonText(plan)}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={Styles.pricing_section} data-context={context}>
      {/* Desktop Table Layout */}
      <div className={Styles.desktop_layout}>
        {/* Trial Buttons Section - Top with original button design */}
        <div className={Styles.best_suited_table_top}>
          <div className={Styles.best_suited_row_top}>
            <div className={Styles.empty_column_top}></div>
            {filteredPlans.map((plan) => (
              <div key={plan.id} className={Styles.best_suited_cell_top}>
                <p className={Styles.best_suited_text_top}>
                  <span className={Styles.best_suited_label_top}>
                    Best Suited For:
                  </span>{' '}
                  {getBestSuitedText(plan.type)}
                </p>

                <button
                  className={`${Styles.trial_button} ${
                    getButtonText(plan) === 'Cancel Subscription'
                      ? Styles.cancel_subscription_button
                      : ''
                  }`}
                  onClick={() => handleButtonClick(plan)}
                >
                  {getButtonText(plan)}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className={Styles.pricing_table}>
          <div className={Styles.table_container}>
            {/* Header Row */}
            <div className={Styles.table_header}>
              <div className={Styles.feature_column_header}>
                <span>Compare plans</span>
              </div>
              {filteredPlans.map((plan) => {
                const discountedPerMonth =
                  duration === 'year'
                    ? Math.floor(plan.amount / 12)
                    : plan.amount;
                const originalMonthlyPrice = plan.discounted_amount;
                const planType = plan.type?.replace(/\s/g, '').toLowerCase();

                return (
                  <div
                    key={plan.id}
                    className={`${Styles.plan_column_header} ${Styles[`${planType}_header`]}`}
                  >
                    <div className={Styles.plan_name}>
                      <h3 className={Styles.plan_name_names}>{plan.type}</h3>
                    </div>
                    <div className={Styles.plan_pricing}>
                      <div className={Styles.price_display}>
                        {duration === 'year' &&
                        originalMonthlyPrice !== discountedPerMonth ? (
                          <>
                            <span className={Styles.original_price}>
                              ${originalMonthlyPrice}
                            </span>
                            <span className={Styles.price_amount}>
                              ${discountedPerMonth}
                            </span>
                          </>
                        ) : (
                          <span className={Styles.price_amount}>
                            ${discountedPerMonth}
                          </span>
                        )}
                      </div>
                      <span className={Styles.billing_info}>
                        /mo (billed{' '}
                        {duration === 'month' ? 'monthly' : 'annually'})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Feature Rows */}
            <div className={Styles.table_body}>
              {features.map((feature) => (
                <div key={feature.id} className={Styles.feature_row}>
                  <div className={Styles.feature_label}>{feature.label}</div>
                  {filteredPlans.map((plan) => {
                    const planType = plan.type
                      ?.replace(/\s/g, '')
                      .toLowerCase();
                    const value = getFeatureValue(feature, planType);
                    return (
                      <div
                        key={`${plan.id}-${feature.id}`}
                        className={Styles.feature_cell}
                      >
                        {renderFeatureCell(feature, value)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Cards Layout */}
      <div className={Styles.mobile_layout}>{renderMobileCards()}</div>

      {/* Confirmation Modal for Cancel Subscription */}
      <ConfirmationModal
        show={cancelModalOpen}
        onHide={() => {
          setCancelModalOpen(false);
        }}
        onDelete={handleCancelSubscription}
        modalHeading="Cancel Subscription"
        modalDescription="Are you sure you want to cancel your subscription? This action cannot be undone."
        actionText="Proceed"
        loaderHeading="Unsubscribing!"
      />
    </div>
  );
};

export default PricingTable;
